import React, { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, MessageSquare, Monitor, Sunrise, ChevronDown, CheckCircle, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

// --- FRAMER MOTION VARIANTS ---
const pageVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: { type: "spring", stiffness: 80, damping: 12, delay: 0.1 } 
    },
};

const contentVariants = {
    open: { opacity: 1, height: "auto" },
    closed: { opacity: 0, height: 0 },
};

// --- ACCORDION ITEM COMPONENT ---
const AccordionItem = ({ icon, title, points, colorClass, isOpen, setOpen }) => (
    <motion.div 
        className="border-b border-gray-200"
        layout
    >
        <button
            className="flex justify-between items-center w-full p-5 sm:p-6 text-left bg-white hover:bg-gray-50 transition-colors rounded-t-xl"
            onClick={setOpen}
            aria-expanded={isOpen}
            aria-controls={`accordion-content-${title.replace(/\s/g, '-')}`}
        >
            <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full flex-shrink-0 ${colorClass.bg} ${colorClass.text}`}>
                    {icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                    {title}
                </h3>
            </div>
            <motion.div
                initial={false}
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
            >
                <ChevronDown className="w-6 h-6 text-gray-500" />
            </motion.div>
        </button>

        <motion.div
            id={`accordion-content-${title.replace(/\s/g, '-')}`}
            animate={isOpen ? "open" : "closed"}
            variants={contentVariants}
            initial="closed"
            className="overflow-hidden bg-white px-5 sm:px-6 pb-6"
        >
            <ul className="space-y-4 pt-4 list-none p-0 m-0 border-l-4 border-teal-200 pl-4 ml-4">
                {points.map((point, index) => (
                    <li key={index} className="flex items-start text-gray-700 text-base leading-relaxed">
                        <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5 mr-3" />
                        <span>{point}</span>
                    </li>
                ))}
            </ul>
        </motion.div>
    </motion.div>
);

// --- MAIN LEARNING TIPS PAGE COMPONENT ---
export default function LearningTipsPage() {
    const [openIndex, setOpenIndex] = useState(0); // State to control which accordion item is open
    
    // Data for the learning tip cards
    const learningTipsData = [
        {
            icon: <BookOpen className="w-6 h-6" />,
            title: "Structured Digital Learning",
            colorClass: { bg: 'bg-blue-100', text: 'text-blue-600' },
            points: [
                "Designated Study Zone: Use a distraction-free physical space for online homework.",
                "Pomodoro Technique: Apply 25-minute focus sessions followed by short breaks.",
                "Curated Resources: Select 2-3 high-quality educational apps/sites only.",
                "Digital Portfolio: Encourage saving their best work online to track progress.",
            ],
        },
        {
            icon: <Monitor className="w-6 h-6" />,
            title: "Promoting Digital Wellness",
            colorClass: { bg: 'bg-pink-100', text: 'text-pink-600' },
            points: [
                "Tech-Free Mealtimes: Make family meals a strict device-free zone for connection.",
                "The 'Digital Sunset': Turn off all screens 60 minutes before bedtime for better sleep.",
                "Active Breaks: Integrate physical activity after every hour of screen time.",
                "Mindful Usage: Ask, 'Does this time online serve a clear purpose?' to reduce mindless scrolling.",
            ],
        },
        {
            icon: <MessageSquare className="w-6 h-6" />,
            title: "Open Communication Strategy",
            colorClass: { bg: 'bg-green-100', text: 'text-green-600' },
            points: [
                "Co-Creation of Rules: Involve children in setting the rules for better buy-in.",
                "The 'Why' Factor: Explain the reasons behind limits, focusing on health and safety, not punishment.",
                "Digital Empathy: Teach them to communicate kindly and respectfully in online environments.",
                "The Safe Space: Establish that they can talk to you immediately if they encounter anything uncomfortable online.",
            ],
        },
    ];

    return (
        <div className="min-h-screen p-6 sm:p-12 bg-gradient-to-br from-gray-50 via-teal-50 to-cyan-50 font-sans">
            <motion.div 
                className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl border-t-6 border-teal-500 overflow-hidden"
                variants={pageVariants}
                initial="hidden"
                animate="visible"
            >
                {/* === PAGE HEADER (Now integrated into the main panel) === */}
                <header className="text-center p-6 sm:p-10 bg-white border-b border-gray-100">
                    <div className="inline-block p-3 bg-teal-600 rounded-full shadow-lg mb-3">
                        <Sunrise className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-800 tracking-tight leading-tight">
                        Interactive Learning Framework
                    </h1>
                    <p className="text-gray-500 mt-2 text-lg">
                        Tap any section below to reveal detailed strategies and actionable advice.
                    </p>
                </header>

                {/* --- ACCORDION CONTAINER --- */}
                <div className="p-0">
                    {learningTipsData.map((tip, index) => (
                        <AccordionItem
                            key={index}
                            {...tip}
                            isOpen={index === openIndex}
                            setOpen={() => setOpenIndex(openIndex === index ? null : index)}
                        />
                    ))}
                </div>
                
                {/* --- FOOTER CTA --- */}
                <motion.div 
                    className="text-center p-8 sm:p-10 bg-gray-50 border-t-2 border-gray-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                >
                    <p className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center gap-3">
                        <Smartphone className="w-6 h-6 text-teal-600"/>
                        Ready to apply these concepts?
                    </p>
                   <Link
                    to="/login" 
                    className="inline-block px-8 py-3 bg-teal-600 text-white font-extrabold rounded-lg hover:bg-teal-700 transition-colors shadow-lg text-lg uppercase tracking-wider"
                >
                    Go to Control Panel 
                </Link>
                </motion.div>

            </motion.div>
        </div>
    );
}